import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FingerprintIcon, EyeIcon, EyeOffIcon, LockIcon, ScanFaceIcon, CheckCircle2Icon, SmartphoneIcon, ArrowLeftIcon, CheckIcon, BuildingIcon, ChevronRightIcon, BadgePercentIcon, HeadphonesIcon, MapPinIcon, WifiIcon, HandIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { CoolingOffScreen } from './CoolingOffScreen';
interface LoginScreenProps {
  onLogin: () => void;
  onNavigateToSignup: () => void;
  onForgotPassword?: () => void;
}
let loginBiometricCounter = 0;
const BACKGROUND_URL = 'https://cdn.magicpatterns.com/uploads/mrQ68U1yesFgefEncBzn2f/Background_(2).png';
const ACCOUNTS = [{
  id: '1',
  holder: 'Shajia Khan',
  type: 'Current Account',
  accountNumber: '01008594839200',
  iban: 'BP05BARC12345601008594839200'
}, {
  id: '2',
  holder: 'Shajia Khan',
  type: 'Savings Account',
  accountNumber: '8726289202929',
  iban: 'BP05BARC98765432109876543210'
}, {
  id: '3',
  holder: 'Shajia Khan',
  type: 'Business Account',
  accountNumber: '5544332211009988',
  iban: 'BP05BARC55443322110099887766'
}
// {
//   id: '4',
//   holder: 'Shajia Khan',
//   type: 'Fixed Deposit Account',
//   accountNumber: '1122334455667788',
//   iban: 'BP05BARC11223344556677889900',
// },
];
const CARDS = [{
  id: 'c2',
  holder: 'Shajia Khan',
  type: 'Credit Card',
  accountNumber: '01008594839201',
  cardType: 'Standard'
}];
// digiBOP brand logo (PNG)
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/ugN9AsyerkWronA9xCodie/Dark_theme_logo-removebg-preview.png';
function Wordmark({
  fontSize = 30
}: {
  fontSize?: number;
}) {
  return <img src={LOGO_URL} alt="digiBOP" style={{
    height: Math.round(fontSize * 1.25),
    width: 'auto'
  }} className="select-none pointer-events-none" draggable={false} data-id="element-1172" data-mp="j2isrslgp2r3" />;
}
export function LoginScreen({
  onLogin,
  onNavigateToSignup,
  onForgotPassword
}: LoginScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginPhase, setLoginPhase] = useState<'idle' | 'otp' | 'bvs' | 'cooling' | 'scanning' | 'verified' | 'consent'>('idle');
  const [scanType, setScanType] = useState<'thumb' | 'face'>('thumb');
  // Two-step entry: landing -> login modal
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [videoDisabled, setVideoDisabled] = useState(false);
  // Face ID setup confirmation modal
  const [showFaceSetupModal, setShowFaceSetupModal] = useState(false);
  // Location / Wi-Fi access consent modals — shown sequentially on first load
  const [showLocationConsent, setShowLocationConsent] = useState(true);
  const [showWifiConsent, setShowWifiConsent] = useState(false);
  // Digital consent state
  // Multi-select: tapping a tile toggles it into/out of the selected set
  // (selected tiles turn orangish). A single `defaultItemId` — the toggle —
  // may be enabled for exactly ONE selected tile across accounts + cards.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [defaultItemId, setDefaultItemId] = useState<string | null>(null);
  const [consentTab, setConsentTab] = useState<'accounts' | 'cards'>('accounts');
  const [visitedTabs, setVisitedTabs] = useState<{
    accounts: boolean;
    cards: boolean;
  }>({
    accounts: true,
    cards: false
  });
  // Cooling-off end timestamp for the login flow
  const [loginCoolingOffUntil, setLoginCoolingOffUntil] = useState<number | null>(null);
  // BVS state — same as BOPSignupFlow
  const [bvsPhase, setBvsPhase] = useState<'right_guidance' | 'right_camera' | 'right_success' | 'left_guidance' | 'left_camera' | 'left_success'>('right_guidance');
  const [bvsCameraTimer, setBvsCameraTimer] = useState(3);
  // Device linking modal
  const [showDeviceLinking, setShowDeviceLinking] = useState(false);
  // BVS camera timer — mirrors BOPSignupFlow logic
  useEffect(() => {
    if (loginPhase === 'bvs' && (bvsPhase === 'right_camera' || bvsPhase === 'left_camera')) {
      if (bvsCameraTimer > 0) {
        const t = setInterval(() => setBvsCameraTimer(p => p - 1), 1000);
        return () => clearInterval(t);
      } else {
        if (bvsPhase === 'right_camera') setBvsPhase('right_success');
        if (bvsPhase === 'left_camera') setBvsPhase('left_success');
      }
    }
  }, [loginPhase, bvsPhase, bvsCameraTimer]);
  // Login button tapped — begin the login flow by requesting an OTP.
  const handleLogin = () => {
    if (loginPhase !== 'idle') return;
    setLoginPhase('otp');
  };
  // Called when OTP is verified — show device linking modal before BVS
  const handleOTPVerified = () => {
    setShowDeviceLinking(true);
  };
  // Called when user taps Proceed in device linking modal
  const handleDeviceLinkingProceed = () => {
    setShowDeviceLinking(false);
    setBvsPhase('right_guidance');
    setBvsCameraTimer(3);
    setLoginPhase('bvs');
  };
  // BVS complete — go to cooling-off then consent
  const handleBVSComplete = () => {
    setLoginCoolingOffUntil(Date.now() + 2 * 60 * 60 * 1000);
    setLoginPhase('cooling');
  };
  // Cooling-off OK tapped — go to digital consent
  const handleCoolingOK = () => {
    setLoginPhase('consent');
  };
  const handleBiometric = (type: 'thumb' | 'face') => {
    setScanType(type);
    loginBiometricCounter++;
    setLoginPhase('scanning');
    setTimeout(() => {
      setLoginPhase('verified');
      setTimeout(() => setLoginPhase('consent'), 1600);
    }, 2000);
  };
  const handleTabChange = (tab: 'accounts' | 'cards') => {
    setConsentTab(tab);
    setVisitedTabs(prev => ({
      ...prev,
      [tab]: true
    }));
  };
  const canSave = visitedTabs.accounts && visitedTabs.cards;
  const pageBg = isPlatinum ? 'linear-gradient(180deg, #1C1C1E 0%, #2A2A2C 50%, #333336 100%)' : isDark ? 'linear-gradient(180deg, #1A0E08 0%, #2D1810 50%, #3D2518 100%)' : 'linear-gradient(180deg, #FFF8F2 0%, #FEEEE0 40%, #FBDDC8 80%, #F8CFB6 100%)';
  const subtleText = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#C4B5AA]' : 'text-[#6E6F72]';
  const dividerSoft = isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E8C9B5';
  const handleTabSelect = (tab: 'login' | 'signup') => {
    if (tab === 'signup') {
      onNavigateToSignup();
      return;
    }
    setActiveTab('login');
  };
  // ─── OTP SCREEN ──────────────────────────────────────────────────────────────
  if (loginPhase === 'otp') {
    return <>
        <OTPPhase isDark={isDark} isPlatinum={isPlatinum} onBack={() => setLoginPhase('idle')} onVerified={handleOTPVerified} data-id="element-1173" data-mp="159l7p5sml7eo" />

        {showDeviceLinking && <DeviceLinkingModal onProceed={handleDeviceLinkingProceed} data-id="element-1174" data-mp="2l16ylgum9wg6" />}
      </>;
  }
  // ─── BVS ─────────────────────────────────────────────────────────────────────
  if (loginPhase === 'bvs') {
    return <BVSScreen isDark={isDark} isPlatinum={isPlatinum} bvsPhase={bvsPhase} setBvsPhase={setBvsPhase} bvsCameraTimer={bvsCameraTimer} setBvsCameraTimer={setBvsCameraTimer} onComplete={handleBVSComplete} data-id="element-1175" data-mp="2urrw10yk0uxi" />;
  }
  // ─── COOLING-OFF ─────────────────────────────────────────────────────────────
  if (loginPhase === 'cooling') {
    return <LoginCoolingOffScreen isDark={isDark} coolingOffUntil={loginCoolingOffUntil} onOK={handleCoolingOK} data-id="element-1176" data-mp="2bz7lv0qmxej1" />;
  }
  // ─── DIGITAL CONSENT SCREEN ─────────────────────────────────────────────────
  if (loginPhase === 'consent') {
    const items = consentTab === 'accounts' ? ACCOUNTS : CARDS;
    // Tapping a tile toggles it in/out of the selected set (multi-select).
    // Deselecting a tile also clears it as default if it was the default.
    const toggleTileSelected = (id: string) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          setDefaultItemId(d => d === id ? null : d);
        } else {
          next.add(id);
        }
        return next;
      });
    };
    // The toggle can only be enabled on a selected tile, and only ONE tile
    // may be the default across both accounts and cards.
    const toggleDefault = (id: string) => {
      setSelectedIds(prev => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setDefaultItemId(d => d === id ? null : id);
    };
    return <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.45,
      ease: 'easeOut'
    }} className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
      background: 'white',
      fontFamily: "'Poppins', sans-serif",
      maxWidth: '430px',
      margin: '0 auto'
    }} data-id="element-1177" data-mp="o3j91rp5rj78">
        {/* Background blobs — two overlapping soft peach radial ellipses top-left */}
        <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '420px',
        pointerEvents: 'none',
        zIndex: 0
      }} data-id="element-1178" data-mp="uji1z08438ql">
          <div style={{
          position: 'absolute',
          top: '-110px',
          left: '-90px',
          width: '450px',
          height: '410px',
          background: 'radial-gradient(ellipse 52% 48% at 46% 46%, rgba(247,168,108,0.42) 0%, rgba(247,168,108,0.18) 44%, rgba(247,168,108,0.04) 68%, transparent 84%)',
          filter: 'blur(30px)'
        }} data-id="element-1179" data-mp="3aadz18mhwhdb" />
          <div style={{
          position: 'absolute',
          top: '-20px',
          left: '55px',
          width: '340px',
          height: '310px',
          background: 'radial-gradient(ellipse 54% 54% at 44% 44%, rgba(247,162,96,0.52) 0%, rgba(247,162,96,0.25) 38%, rgba(247,162,96,0.05) 65%, transparent 80%)',
          filter: 'blur(24px)'
        }} data-id="element-1180" data-mp="2cg3gvp479uef" />
          <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-40px',
          width: '270px',
          height: '240px',
          background: 'radial-gradient(ellipse at 44% 44%, rgba(247,158,90,0.3) 0%, rgba(247,158,90,0.09) 48%, transparent 70%)',
          filter: 'blur(34px)'
        }} data-id="element-1181" data-mp="2qea2a5ryq1x7" />
        </div>

        <div style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }} data-id="element-1182" data-mp="3cn8gq5bomort">
          {/* Header */}
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '24px 20px 0'
        }} data-id="element-1183" data-mp="32vkvim5py3ys">
            <button onClick={() => setLoginPhase('idle')} style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }} data-id="element-1184" data-mp="2tx8zg4m32dyy">
              <ArrowLeftIcon style={{
              width: '20px',
              height: '20px',
              color: '#5A1408'
            }} strokeWidth={2} data-id="element-1185" data-mp="d8d9ooak6d0q" />
            </button>
            <span style={{
            fontSize: '17px',
            color: '#5A1408',
            fontWeight: 600
          }} data-id="element-1186" data-mp="bpvan4x53qs8">
              Digital Account Selection
            </span>
          </div>

          {/* Subtitle */}
          <p style={{
          fontSize: '14px',
          color: '#6B6B6B',
          lineHeight: 1.6,
          margin: '12px 20px 0',
          fontWeight: 400
        }} data-id="element-1187" data-mp="zfzfffm2dwkt">
            Select the Accounts &amp; Cards you wish to use for mobile and
            internet banking.
          </p>

          {/* Spacer to show blobs */}
          <div style={{
          height: '50px'
        }} data-id="element-1188" data-mp="lmhubqdqcbso" />

          {/* Segmented tab control — white active pill on a soft peach track */}
          <div style={{
          display: 'flex',
          padding: '5px',
          borderRadius: '14px',
          background: 'rgba(243,112,36,0.08)',
          margin: '0 4px 20px'
        }} data-id="element-1189" data-mp="1de77pa85o9pn">
            {([{
            key: 'accounts',
            label: 'Active Accounts'
          }, {
            key: 'cards',
            label: 'Cards'
          }] as const).map(tab => {
            const active = consentTab === tab.key;
            return <button key={tab.key} onClick={() => handleTabChange(tab.key)} style={{
              flex: 1,
              height: '44px',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '10px',
              background: active ? '#FFFFFF' : 'transparent',
              boxShadow: active ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              fontSize: '14px',
              fontWeight: active ? 600 : 400,
              color: active ? '#1A1A1A' : '#8A8A8A',
              fontFamily: "'Poppins', sans-serif",
              transition: 'all 0.2s ease'
            }} data-id="element-1190" data-mp="3jy4csc5h370j">
                  {tab.label}
                </button>;
          })}
          </div>

          {/* Content list */}
          <div style={{
          flex: 1,
          padding: '0 4px 140px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }} data-id="element-1191" data-mp="367ght4gjasaw">
            {items.map(item => {
            const isSelected = selectedIds.has(item.id);
            const isDefault = defaultItemId === item.id;
            return <div key={item.id} role="button" aria-pressed={isSelected} style={{
              background: isSelected ? 'rgba(243,112,36,0.10)' : '#FFFFFF',
              border: isSelected ? '1.5px solid #F37024' : '1px solid #E5E0DC',
              borderRadius: '16px',
              padding: '18px',
              cursor: 'pointer',
              transition: 'background 0.18s ease, border-color 0.18s ease'
            }} onClick={() => toggleTileSelected(item.id)} data-id="element-1192" data-mp="vf5humm3vn8e">
                  {/* Top row: icon + name + toggle */}
                  <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }} data-id="element-1193" data-mp="17tu4b5kspdst">
                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px'
                }} data-id="element-1194" data-mp="24fdvxdc24yhh">
                      <div style={{
                    width: '44px',
                    height: '44px',
                    background: isSelected ? 'rgba(243,112,36,0.14)' : 'white',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: '1px solid rgba(243,112,36,0.18)'
                  }} data-id="element-1195" data-mp="2a6dbzbof8rd4">
                        <BuildingIcon style={{
                      width: '20px',
                      height: '20px',
                      color: '#F37024'
                    }} strokeWidth={1.6} data-id="element-1196" data-mp="1ckpf6po20712" />
                      </div>
                      <div data-id="element-1197" data-mp="12exvxxl1jk6o">
                        <p style={{
                      margin: '0 0 3px',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#1A1A1A'
                    }} data-id="element-1198" data-mp="12ic09bd9m3gr">
                          {item.holder}
                        </p>
                        <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#9E9E9E',
                      fontWeight: 400
                    }} data-id="element-1199" data-mp="jqj63dmgmc7g">
                          {item.type}
                        </p>
                      </div>
                    </div>
                    {/* Toggle + Set as Default */}
                    <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '5px'
                }} data-id="element-1200" data-mp="1f5gki0gfoydo">
                      <button type="button" disabled={!isSelected} onClick={e => {
                    e.stopPropagation();
                    toggleDefault(item.id);
                  }} style={{
                    width: '56px',
                    height: '30px',
                    background: isDefault ? '#34C759' : '#d0d0d0',
                    borderRadius: '15px',
                    position: 'relative',
                    cursor: isSelected ? 'pointer' : 'not-allowed',
                    opacity: isSelected ? 1 : 0.5,
                    border: 'none',
                    flexShrink: 0
                  }} aria-label="Set as default" aria-pressed={isDefault} data-id="element-1201" data-mp="1k52y4b06cgn0">
                        <div style={{
                      position: 'absolute',
                      top: '3px',
                      left: isDefault ? '29px' : '3px',
                      width: '24px',
                      height: '24px',
                      background: 'white',
                      borderRadius: '50%',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.22)',
                      transition: 'left 0.18s'
                    }} data-id="element-1202" data-mp="2mi5ssc8b58s7" />
                      </button>
                      <span style={{
                    fontSize: '10px',
                    color: '#9E9E9E',
                    fontWeight: 300,
                    whiteSpace: 'nowrap'
                  }} data-id="element-1203" data-mp="1ttqfpv8ql0ji">
                        Set as Default
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{
                borderTop: `1px solid ${isSelected ? 'rgba(243,112,36,0.22)' : '#f0e8e3'}`,
                paddingTop: '13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }} data-id="element-1204" data-mp="1o0kzxcmd70y6">
                    <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }} data-id="element-1205" data-mp="26w19l5ls5e37">
                      <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#7A2A10',
                    letterSpacing: '0.7px'
                  }} data-id="element-1206" data-mp="2zjx9ej6nbm47">
                        ACCOUNT NO.
                      </span>
                      <span style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#1A1A1A'
                  }} data-id="element-1207" data-mp="12lzn8nln03gl">
                        {item.accountNumber}
                      </span>
                    </div>
                    <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }} data-id="element-1208" data-mp="izbofzuo9add">
                      <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#7A2A10',
                    letterSpacing: '0.7px'
                  }} data-id="element-1209" data-mp="1udfdcrmek8bk">
                        {consentTab === 'cards' ? 'CARD TYPE' : 'IBAN'}
                      </span>
                      <span style={{
                    fontSize: '11px',
                    color: '#5A5A5A',
                    textAlign: 'right',
                    maxWidth: '220px'
                  }} data-id="element-1210" data-mp="x1702l2manhm">
                        {consentTab === 'cards' ? (item as any).cardType : item.iban}
                      </span>
                    </div>
                  </div>
                </div>;
          })}

            {/* Footer text */}
            <p style={{
            fontSize: '11px',
            color: '#BBBBBB',
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.6
          }} data-id="element-1211" data-mp="3hr644jgjkcey">
              You can change your default account at any time from settings.
            </p>
          </div>

          {/* Sticky Save button */}
          <div style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '430px',
          background: 'white',
          padding: '10px 20px 28px',
          zIndex: 10
        }} data-id="element-1212" data-mp="357ilj4up6xqa">
            <div style={{
            display: 'flex',
            justifyContent: 'center'
          }} data-id="element-1213" data-mp="2qhjnr3721obc">
              <motion.button whileTap={{
              scale: canSave ? 0.98 : 1
            }} disabled={!canSave} onClick={() => canSave && onLogin()} style={{
              width: '280px',
              height: '55px',
              borderRadius: '8px',
              background: canSave ? '#F37024' : '#F0C9A8',
              color: 'white',
              border: 'none',
              cursor: canSave ? 'pointer' : 'default',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '2px',
              fontFamily: "'Poppins', sans-serif"
            }} data-id="element-1214" data-mp="2hipwztgens5p">
                Save
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // ─── MAIN LOGIN SCREEN (image-based, two-step) ───────────────────────────────
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
    fontFamily: "'Poppins', system-ui, sans-serif"
  }} data-id="element-1215" data-mp="3ezf50sijhcvg">
      {/* Background mosque image — edge-to-edge */}
      {!videoDisabled && <img src={BACKGROUND_URL} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none" draggable={false} data-id="element-1216" data-mp="32nuyu5ala8i2" />}
      {videoDisabled && <div className="absolute inset-0" style={{
      background: pageBg
    }} data-id="element-1217" data-mp="37lwcubt5rhmo" />}

      {/* Subtle bottom darkening for legibility on the landing step */}
      {!modalOpen && <div aria-hidden className="absolute inset-x-0 bottom-0 pointer-events-none" style={{
      height: '70%',
      background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(40,12,4,0.35) 55%, rgba(30,8,2,0.65) 100%)'
    }} data-id="element-1218" data-mp="3gdyci7vektvw" />}

      {/* ─── STEP 2: Modal open — centered top logo ─── */}
      {modalOpen && <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      delay: 0.1
    }} className="absolute left-1/2 -translate-x-1/2 z-20 ml-[-35px]" style={{
      top: 64
    }} data-id="element-1219" data-mp="1w07qv7lw1eaz">
          <Wordmark fontSize={30} data-id="element-1220" data-mp="2znmba8ms82df" />
        </motion.div>}

      {/* ─── STEP 1: Landing content ─── */}
      <AnimatePresence data-id="element-1221" data-mp="34ledbvh554vc">
        {!modalOpen && <motion.div key="landing" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.4
      }} className="relative z-10 flex-1 flex flex-col" data-id="element-1222" data-mp="38n9coufih86h">
            {/* Logo + taglines anchored toward lower-middle */}
            <div className="mt-auto px-6" style={{
          paddingBottom: 18
        }} data-id="element-1223" data-mp="3s9e610y0j5ck">
              <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="mb-[14px]" data-id="element-1224" data-mp="1bosm8kxryznb">
                <Wordmark fontSize={34} data-id="element-1225" data-mp="1uf0bq08jxrl1" />
              </motion.div>
              <motion.h1 initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.18
          }} style={{
            color: '#FFFFFF',
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            margin: 0
          }} className="font-[600] pt-[0px] pb-[0px] mt-[0px] mb-[75px] text-[35px]" data-id="element-1226" data-mp="1uhw9nokegg9h">
                Banking Simplified!
              </motion.h1>
              <motion.p initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.26
          }} style={{
            color: 'rgba(255,255,255,0.92)',
            fontWeight: 400
          }} className="text-[18px] mt-[10px] mb-[65px]" data-id="element-1227" data-mp="1cobnktv55gxy">
                All your Digital Payments and Banking Services in one place.
              </motion.p>
            </div>

            {/* Login button + CTA */}
            <div className="relative z-10 px-6 flex flex-col items-center" style={{
          paddingBottom: 24
        }} data-id="element-1228" data-mp="oy7mou9c7van">
              <motion.button initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.34
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            setActiveTab('login');
            setModalOpen(true);
          }} className="flex items-center justify-center self-start active:opacity-95" style={{
            width: 254,
            height: 56,
            borderRadius: 8,
            background: '#F76708',
            //boxShadow: '0 10px 26px rgba(247,103,8,0.38)',
            border: 'none',
            cursor: 'pointer'
          }} data-id="element-1229" data-mp="choqlt4ttz1g">
                <span style={{
              color: '#FFFFFF',
              fontWeight: 500,
              letterSpacing: '0.01em',
              flex: 1,
              textAlign: 'center',
              marginLeft: 28
            }} className="text-[20px]" data-id="element-1230" data-mp="1zhcq8btmydkg">
                  Login
                </span>
                <span className="flex items-center pr-5" style={{
              color: '#FFFFFF'
            }} data-id="element-1231" data-mp="2i9xeth2axwig">
                  <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} style={{
                marginRight: -10
              }} data-id="element-1232" data-mp="298qvz1c6k9m1" />

                  <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} style={{
                marginRight: -10,
                opacity: 0.85
              }} data-id="element-1233" data-mp="xr7ogwrkhkho" />

                  <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} style={{
                opacity: 0.7
              }} data-id="element-1234" data-mp="3igar99qcbzgn" />
                </span>
              </motion.button>

              <motion.button initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.45
          }} onClick={() => setVideoDisabled(v => !v)} className="mt-6" style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 400,
            color: 'rgba(255,255,255,0.55)',
            textDecoration: 'underline',
            textUnderlineOffset: '3px'
          }} data-id="element-1235" data-mp="10c9l2hesltv">
                {videoDisabled ? 'Enable video' : 'Disable video permanently'}
              </motion.button>
            </div>

            {/* Bottom navbar — Discounts / Support / Locate Us */}
            <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="relative z-10 w-full flex items-center justify-around pt-[5px]" style={{
          background: 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(50px)',
          paddingBottom: 22,
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15
        }} data-id="element-1236" data-mp="1v6ppaczias4">
              {[{
            label: 'Discounts',
            icon: BadgePercentIcon
          }, {
            label: 'Support',
            icon: HeadphonesIcon
          }, {
            label: 'Locate Us',
            icon: MapPinIcon
          }].map(item => {
            const Icon = item.icon;
            return <button key={item.label} className="flex flex-col items-center active:scale-95 transition-transform gap-[3px]" style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }} data-id="element-1237" data-mp="6lah2hzx6wqg">
                    <Icon className="w-6 h-6" strokeWidth={1.7} style={{
                color: '#FFFFFF'
              }} data-id="element-1238" data-mp="2j5rbuclek93" />

                    <span style={{
                fontSize: 12,
                color: '#FFFFFF',
                fontWeight: 400
              }} data-id="element-1239" data-mp="3lrn1gry6stmn">
                      {item.label}
                    </span>
                  </button>;
          })}
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ─── STEP 2: Login modal (slides up from bottom) ─── */}
      <AnimatePresence data-id="element-1240" data-mp="2y1specloz93g">
        {modalOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setModalOpen(false)} className="absolute inset-0 z-10" style={{
          background: 'rgba(0,0,0,0.12)'
        }} data-id="element-1241" data-mp="2tzfuykceaxlz" />

            <motion.div key="login-modal" initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 32,
          stiffness: 320
        }} className="absolute bottom-0 left-1/2 -translate-x-1/2 z-30 w-full ml-[-202px]" style={{
          maxWidth: 403,
          height: 450,
          background: '#F9F9F9',
          borderTopLeftRadius: 40,
          borderTopRightRadius: 40,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          padding: '0 26px',
          display: 'flex',
          flexDirection: 'column'
        }} data-id="element-1242" data-mp="2f1czpr1dayio">
              {/* Grab handle */}
              <div className="w-full flex justify-center pt-[20px] pb-[48px]" data-id="element-1243" data-mp="1cgvqmsauz704">
                <div style={{
              width: 44,
              height: 4,
              borderRadius: 4,
              background: '#1A1A1A'
            }} data-id="element-1244" data-mp="1ciydx4kmmm15" />
              </div>

              {/* Tabs */}
              <div className="flex items-center" style={{
            gap: 0,
            marginBottom: 10
          }} data-id="element-1245" data-mp="xrzae7ynenof">
                <button onClick={() => handleTabSelect('login')} className="flex-1 flex flex-col items-center" style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              paddingBottom: 10
            }} data-id="element-1246" data-mp="17j3ol5gi24i7">
                  <span style={{
                fontSize: 17,
                fontWeight: 600,
                color: activeTab === 'login' ? '#F76708' : '#1A1A1A'
              }} data-id="element-1247" data-mp="2loysxmd4s0tz">
                    Login
                  </span>
                  <div style={{
                marginTop: 8,
                height: 3,
                borderRadius: 3,
                width: '70%',
                background: activeTab === 'login' ? '#F76708' : 'transparent'
              }} data-id="element-1248" data-mp="2gpvs6e46ogrt" />
                </button>
                <button onClick={() => handleTabSelect('signup')} className="flex-1 flex flex-col items-center" style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              paddingBottom: 10
            }} data-id="element-1249" data-mp="30eczkmpbo45o">
                  <span style={{
                fontSize: 17,
                fontWeight: 600,
                color: '#1A1A1A'
              }} data-id="element-1250" data-mp="2fqm9t8tg36dd">
                    Sign Up
                  </span>
                  <div style={{
                marginTop: 8,
                height: 3,
                borderRadius: 3,
                width: '70%',
                background: 'transparent'
              }} data-id="element-1251" data-mp="21qbjq7zylvt9" />
                </button>
              </div>

              {/* Username */}
              <div className="flex items-center" style={{
            marginTop: 22,
            borderRadius: 12,
            padding: '0 16px',
            height: 54,
            backgroundImage: 'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(to right, #F76708, #FFFFFF)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            border: '1px solid transparent'
          }} data-id="element-1252" data-mp="2koe05yefykfg">
                <SmartphoneIcon className="w-[18px] h-[18px] mr-3 flex-shrink-0" strokeWidth={1.8} style={{
              color: '#000000'
            }} data-id="element-1253" data-mp="3uksws7rpplna" />

                <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" className="flex-1 outline-none text-[15px] bg-transparent text-[#303030] placeholder:text-[#B8B8BC]" data-id="element-1254" data-mp="cgrbph0qp6gq" />
              </div>

              {/* Password */}
              <div className="flex items-center" style={{
            marginTop: 16,
            borderRadius: 12,
            padding: '0 16px',
            height: 54,
            backgroundImage: 'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(to right, #FFFFFF, #F76708)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            border: '1px solid transparent'
          }} data-id="element-1255" data-mp="r3kifxw713xb">
                <LockIcon className="w-[18px] h-[18px] mr-3 flex-shrink-0" strokeWidth={1.8} style={{
              color: '#000000'
            }} data-id="element-1256" data-mp="1gr3rm7uhpfrl" />

                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" className="flex-1 outline-none text-[15px] bg-transparent text-[#303030] placeholder:text-[#B8B8BC]" data-id="element-1257" data-mp="1psthvacmy8ns" />

                <button type="button" onClick={() => setShowPassword(!showPassword)} className="ml-2 flex-shrink-0" data-id="element-1258" data-mp="uglmroveh1hh">
                  {showPassword ? <EyeOffIcon className="w-[18px] h-[18px]" strokeWidth={1.8} style={{
                color: '#919191'
              }} data-id="element-1259" data-mp="kq0qi837891q" /> : <EyeIcon className="w-[18px] h-[18px]" strokeWidth={1.8} style={{
                color: '#919191'
              }} data-id="element-1260" data-mp="6r11gzbrxs51" />}
                </button>
              </div>

              {/* Forgot */}
              <div className="w-full flex justify-end" style={{
            marginTop: 10
          }} data-id="element-1261" data-mp="1w67h4h5g2okh">
                <button onClick={onForgotPassword} style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              color: '#5A5B5E'
            }} data-id="element-1262" data-mp="3ln32gepr58d9">
                  Forgot?
                </button>
              </div>

              {/* Login + Face ID row */}
              <div className="flex items-center" style={{
            marginTop: 'auto',
            marginBottom: 36,
            gap: 18
          }} data-id="element-1263" data-mp="d82hs5sud1zk">
                <button onClick={handleLogin} disabled={loginPhase !== 'idle'} className="active:scale-[0.99] disabled:opacity-60 font-[500]" style={{
              flex: 1,
              height: 56,
              borderRadius: 12,
              background: '#F76708',
              border: 'none',
              cursor: 'pointer',
              color: '#FFFFFF',
              fontSize: 16
              //boxShadow: '0 8px 20px rgba(247,103,8,0.3)'
            }} data-id="element-1264" data-mp="393k1i9m6qrc">
                  Login
                </button>
                <button onClick={() => setShowFaceSetupModal(true)} disabled={loginPhase !== 'idle'} aria-label="Face ID" className="flex items-center justify-center active:scale-95 disabled:opacity-60" style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#FFFFFF',
              border: '1px solid #EFEFEF',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
              flexShrink: 0
            }} data-id="element-1265" data-mp="1xqd4u9hgjc6j">
                  <ScanFaceIcon className="w-7 h-7" strokeWidth={1.7} style={{
                color: '#661806'
              }} data-id="element-1266" data-mp="d7axez1es6ux" />
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* ─── Face ID setup confirmation modal (bottom sheet, same style as login modal) ─── */}
      <AnimatePresence data-id="element-1267" data-mp="10yq4t681led1">
        {showFaceSetupModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowFaceSetupModal(false)} className="fixed inset-0 z-40" style={{
          background: 'rgba(0,0,0,0.4)'
        }} data-id="element-1268" data-mp="110t3een9gftd" />

            <motion.div key="face-setup-modal" initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 32,
          stiffness: 320
        }} className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full pb-[20px] pl-[26px] pr-[26px] ml-[-202px]" style={{
          maxWidth: 403,
          background: '#F9F9F9',
          borderTopLeftRadius: 40,
          borderTopRightRadius: 40,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          padding: '0 26px 36px'
        }} data-id="element-1269" data-mp="3480cy0118e80">
              {/* Grab handle */}
              <div className="w-full flex justify-center pt-[20px] pb-[28px]" data-id="element-1270" data-mp="21qouh8le5iv7">
                <div style={{
              width: 44,
              height: 4,
              borderRadius: 4,
              background: '#D9D9D9'
            }} data-id="element-1271" data-mp="1xq4wy3wwod8j" />
              </div>

              {/* Face icon */}
              <div className="w-full flex justify-center mb-5" data-id="element-1272" data-mp="1p9mh8sz21291">
                <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(247,103,8,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} data-id="element-1273" data-mp="1u8zdu5fq9not">
                  <ScanFaceIcon className="w-8 h-8" strokeWidth={1.7} style={{
                color: '#F76708'
              }} data-id="element-1274" data-mp="16i3cohpdtg4w" />
                </div>
              </div>

              <h3 style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: 10
          }} data-id="element-1275" data-mp="11jjt2ny9ggfp">
                Set up Face ID?
              </h3>
              <p style={{
            fontSize: 14,
            color: '#6E6F72',
            textAlign: 'center',
            lineHeight: 1.5,
            marginBottom: 28,
            padding: '0 10px'
          }} data-id="element-1276" data-mp="ryshvur9no94">
                Use Face ID to log in faster and more securely next time.
              </p>

              <div className="flex items-center" style={{
            gap: 12
          }} data-id="element-1277" data-mp="1amfdb77zl4jn">
                <button onClick={() => setShowFaceSetupModal(false)} className="active:scale-[0.98]" style={{
              flex: 1,
              height: 52,
              borderRadius: 12,
              background: '#FFFFFF',
              border: '1px solid #E5E5E5',
              cursor: 'pointer',
              color: '#1A1A1A',
              fontSize: 15,
              fontWeight: 600
            }} data-id="element-1278" data-mp="1klwi7mfezeek">
                  No
                </button>
                <button onClick={() => {
              setShowFaceSetupModal(false);
              setActiveTab('login');
              setModalOpen(true);
            }} className="active:scale-[0.98]" style={{
              flex: 1,
              height: 52,
              borderRadius: 12,
              background: '#F76708',
              border: 'none',
              cursor: 'pointer',
              color: '#FFFFFF',
              fontSize: 15,
              fontWeight: 600,
              boxShadow: '0 6px 16px rgba(247,103,8,0.3)'
            }} data-id="element-1279" data-mp="30wo2qht6rk20">
                  Yes
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* ─── Location access consent modal ─── */}
      <AnimatePresence data-id="element-1280" data-mp="183zj2vn1chy1">
        {showLocationConsent && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-40" style={{
          background: 'rgba(0,0,0,0.4)'
        }} data-id="element-1281" data-mp="132ttv7sl3ct9" />

            <motion.div key="location-consent-modal" initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 32,
          stiffness: 320
        }} className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full pb-[20px] pl-[26px] pr-[26px] ml-[-202px]" style={{
          maxWidth: 403,
          background: '#F9F9F9',
          borderTopLeftRadius: 48,
          borderTopRightRadius: 48,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          padding: '0 26px 36px'
        }} data-id="element-1282" data-mp="kdwuipibxu46">
              {/* Grab handle */}
              <div className="w-full flex justify-center pt-[20px] pb-[28px]" data-id="element-1283" data-mp="1xk8p8b0t6plq">
                <div style={{
              width: 44,
              height: 4,
              borderRadius: 4,
              background: '#D9D9D9'
            }} data-id="element-1284" data-mp="1st0glh7az89m" />
              </div>

              {/* Location icon */}
              <div className="w-full flex justify-center mb-5" data-id="element-1285" data-mp="110007zr9a6ml">
                <MapPinIcon className="w-7 h-7" strokeWidth={1.7} style={{
              color: '#B6B6B5'
            }} data-id="element-1286" data-mp="qx46c7m0z051" />
              </div>

              <h3 style={{
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: 10
          }} className="text-[16px] font-[500]" data-id="element-1287" data-mp="gxln5tdxvie1">
                Allow Location Access?
              </h3>
              <p style={{
            textAlign: 'center',
            lineHeight: 1.5,
            marginBottom: 28,
            padding: '0 10px'
          }} className="text-[16px] font-[300] text-[#000000]" data-id="element-1288" data-mp="1eldwflq9ua1z">
                Turn on precise location for additional safety and a smoother
                experience.
              </p>

              <div className="flex items-center" style={{
            gap: 12
          }} data-id="element-1289" data-mp="3pd5egmv1uqqg">
                <button onClick={() => {
              setShowLocationConsent(false);
              setShowWifiConsent(true);
            }} className="active:scale-[0.98] text-[15px] font-[500]" style={{
              flex: 1,
              height: 56,
              borderRadius: 8,
              background: '#FFFFFF',
              border: '1px solid #E5E5E5',
              cursor: 'pointer',
              color: '#1A1A1A'
            }} data-id="element-1290" data-mp="1qz0uyzmujjn9">
                  Cancel
                </button>
                <button onClick={() => {
              setShowLocationConsent(false);
              setShowWifiConsent(true);
            }} className="active:scale-[0.98] text-[15px] font-[400]" style={{
              flex: 1,
              height: 56,
              borderRadius: 8,
              background: '#F87A27',
              border: 'none',
              cursor: 'pointer',
              color: '#FFFFFF'
            }} data-id="element-1291" data-mp="yb51s4whk9v8">
                  Turn On
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* ─── Wi-Fi access consent modal ─── */}
      <AnimatePresence data-id="element-1292" data-mp="2m7unpcvf4hm4">
        {showWifiConsent && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-40" style={{
          background: 'rgba(0,0,0,0.4)'
        }} data-id="element-1293" data-mp="2r65wdk9z8m2r" />

            <motion.div key="wifi-consent-modal" initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 32,
          stiffness: 320
        }} className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full pb-[20px] pl-[26px] pr-[26px] ml-[-202px]" style={{
          maxWidth: 403,
          background: '#F9F9F9',
          borderTopLeftRadius: 48,
          borderTopRightRadius: 48,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          padding: '0 26px 36px'
        }} data-id="element-1294" data-mp="bgl2b9h7sh63">
              {/* Grab handle */}
              <div className="w-full flex justify-center pt-[20px] pb-[28px]" data-id="element-1295" data-mp="1bnq7m57gu3qv">
                <div style={{
              width: 44,
              height: 4,
              borderRadius: 4,
              background: '#D9D9D9'
            }} data-id="element-1296" data-mp="16on2ylvedzjf" />
              </div>

              {/* Wifi icon */}
              <div className="w-full flex justify-center mb-5" data-id="element-1297" data-mp="1gbzbprvnqlhy">
                <WifiIcon className="w-8 h-8" strokeWidth={1.7} style={{
              color: '#B6B6B5'
            }} data-id="element-1298" data-mp="1gdk5vge282fa" />
              </div>

              <h3 style={{
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: 10
          }} className="text-[16px] font-[500]" data-id="element-1299" data-mp="1bc4l7pfsnxxf">
                Wi-Fi Not Secure
              </h3>
              <p style={{
            color: '#000000',
            textAlign: 'center',
            lineHeight: 1.5,
            marginBottom: 28,
            padding: '0 10px'
          }} className="text-[16px] font-[300]" data-id="element-1300" data-mp="nmscsrsajtw6">
                We recommend you to switch to a secure wifi before signing up
                for your security.
              </p>

              <div className="flex items-center" style={{
            gap: 12
          }} data-id="element-1301" data-mp="3vxw94i9fw9b3">
                <button onClick={() => setShowWifiConsent(false)} className="active:scale-[0.98] text-[15px] font-[500]" style={{
              flex: 1,
              height: 56,
              borderRadius: 8,
              background: '#FFFFFF',
              border: '1px solid #E5E5E5',
              cursor: 'pointer',
              color: '#1A1A1A'
            }} data-id="element-1302" data-mp="sg4curo9t373">
                  Cancel
                </button>
                <button onClick={() => setShowWifiConsent(false)} className="active:scale-[0.98] text-[15px] font-[400]" style={{
              flex: 1,
              height: 56,
              borderRadius: 8,
              background: '#F87A27',
              border: 'none',
              cursor: 'pointer',
              color: '#FFFFFF'
            }} data-id="element-1303" data-mp="2y81cyer944mn">
                  Turn On
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* ─── Biometric scanning / verified overlay ─── */}
      <AnimatePresence data-id="element-1304" data-mp="26f7jvd5wlje0">
        {(loginPhase === 'scanning' || loginPhase === 'verified') && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
        background: pageBg,
        backdropFilter: 'blur(20px)'
      }} data-id="element-1305" data-mp="2bek924k8z2jr">
            <AnimatePresence mode="wait" data-id="element-1306" data-mp="2dy739itd7axd">
              {loginPhase === 'scanning' && <motion.div key="scanning" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex flex-col items-center gap-6" data-id="element-1307" data-mp="2soecr91e9jlx">
                  <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
              background: 'rgba(243, 112, 36, 0.12)',
              border: '2px solid rgba(243, 112, 36, 0.3)'
            }} data-id="element-1308" data-mp="24ytpwf24e27d">
                    {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-1309" data-mp="aibswchmua7w" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-1310" data-mp="fj994jj30u6r" />}
                  </motion.div>
                  <div className="text-center" data-id="element-1311" data-mp="thg0rhxftxxz">
                    <p className={`text-[18px] font-bold mb-1 ${isPlatinum || isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1312" data-mp="tfms5x39l3nc">
                      {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                    </p>
                    <p className={`text-[13px] ${subtleText}`} data-id="element-1313" data-mp="2uu6ci3ewqe9s">
                      {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                    </p>
                  </div>
                  <div className="w-48 h-1.5 bg-[#F37024]/10 rounded-full overflow-hidden" data-id="element-1314" data-mp="271pwdyeqcwd4">
                    <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 2,
                ease: 'easeInOut'
              }} className="h-full bg-gradient-to-r from-[#F37024] to-[#E8943A] rounded-full" data-id="element-1315" data-mp="2c2nbg24g1aae" />
                  </div>
                </motion.div>}
              {loginPhase === 'verified' && <motion.div key="verified" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex flex-col items-center gap-6" data-id="element-1316" data-mp="1fw258fibt1ee">
                  <motion.div initial={{
              scale: 0.3
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 12,
              stiffness: 200
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
              border: '2px solid rgba(34, 197, 94, 0.3)'
            }} data-id="element-1317" data-mp="2djawys8x76ou">
                    <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-1318" data-mp="2n7jo3k31z5gv" />
                  </motion.div>
                  <div className="text-center" data-id="element-1319" data-mp="2x7kc3jbva9re">
                    <p className={`text-[18px] font-bold mb-1 ${isPlatinum || isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1320" data-mp="2x4oejw3xln3h">
                      Verified Successfully
                    </p>
                    <p className={`text-[13px] ${subtleText}`} data-id="element-1321" data-mp="3axxg5aln14bg">
                      Setting up your accounts...
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
// ─────────────────────────────────────────────────────────────────────────────
// MOBILE OTP PHASE — same design as BOPSignupFlow mobile_otp step
// ─────────────────────────────────────────────────────────────────────────────
type BvsPhase = 'right_guidance' | 'right_camera' | 'right_success' | 'left_guidance' | 'left_camera' | 'left_success';
function OTPPhase({
  isDark,
  isPlatinum,
  onBack,
  onVerified
}: {
  isDark: boolean;
  isPlatinum: boolean;
  onBack: () => void;
  onVerified: () => void;
}) {
  const [mobileOtp, setMobileOtp] = useState(['', '', '', '', '', '']);
  const [mobileTimer, setMobileTimer] = useState(30);
  useEffect(() => {
    if (mobileTimer > 0) {
      const t = setInterval(() => setMobileTimer(p => p - 1), 1000);
      return () => clearInterval(t);
    }
  }, [mobileTimer]);
  const textStyle = isPlatinum || isDark ? 'text-white' : 'text-[#1A1A1A]';
  const subtextStyle = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]';
  const inputBgStyle = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-white';
  const inputBorderStyle = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.15)]' : 'border-[#F0E8E3]';
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...mobileOtp];
    newOtp[index] = value.slice(-1);
    setMobileOtp(newOtp);
    if (value && index < 5) {
      const next = document.getElementById(`login-otp-${index + 1}`);
      if (next) (next as HTMLInputElement).focus();
    }
    if (newOtp.every(d => d !== '')) {
      setTimeout(onVerified, 500);
    }
  };
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]';
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className={`w-full min-h-screen flex flex-col py-6 px-6 ${bgStyle}`} data-id="element-1322" data-mp="1hr7nkllqyzc7">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1323" data-mp="32a9rsv37r4px">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1324" data-mp="3gyea3xycte86">
          <ArrowLeftIcon className={`w-6 h-6 ${textStyle}`} strokeWidth={2} data-id="element-1325" data-mp="2oa8pdb7avp8c" />
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" className="h-10 w-auto" data-id="element-1326" data-mp="2jc75wp5ypnwc" />

        <div className="w-10" data-id="element-1327" data-mp="2t9bvkyz0o1hw" />
      </div>

      <h1 className={`mb-2 ${textStyle}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif',
      fontWeight: 300,
      fontSize: 20,
      lineHeight: 1.3
    }} data-id="element-1328" data-mp="2t8a97ycjvskv">
        Verify Mobile Number
      </h1>
      <p className={`mb-8 ${subtextStyle}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif',
      fontWeight: 400,
      fontSize: 14
    }} data-id="element-1329" data-mp="fnc841x5a9zg">
        We've sent an OTP to your registered number: +92•••••••66.
      </p>

      {/* OTP Inputs — same style as signup */}
      <div className="flex justify-between gap-2 mb-8 p-3 rounded-2xl" style={{
      background: 'rgba(249,249,249,100)'
    }} data-id="element-1330" data-mp="uj1ui168sp4p">
        {mobileOtp.map((digit, index) => <div key={index} className="relative group flex-1" data-id="element-1331" data-mp="1w7gnn3xko07" data-mpi={index}>
            <input id={`login-otp-${index}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} className={`relative w-full h-14 rounded-2xl text-center text-2xl font-bold outline-none border transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10' : `${inputBorderStyle} ${inputBgStyle}`} ${textStyle}`} data-id="element-1332" data-mp="6tz6mbl0m01r" />
          </div>)}
      </div>

      {/* Resend timer */}
      <div className="flex flex-col items-center justify-center gap-2" data-id="element-1333" data-mp="1see2jss3b3ga">
        {mobileTimer > 0 ? <span style={{
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontWeight: 400,
        fontSize: 13,
        color: '#6E6F72'
      }} data-id="element-1334" data-mp="1ngm7ipw5f17v">
            Resend OTP in{' '}
            <span style={{
          color: '#661806',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-1335" data-mp="18pdb9xxqqhlr">
              0:{String(mobileTimer).padStart(2, '0')}
            </span>
          </span> : <button onClick={() => {
        setMobileTimer(30);
        setMobileOtp(['', '', '', '', '', '']);
      }} className="text-[#F37024] font-bold text-sm hover:underline" data-id="element-1336" data-mp="uqdqj1dcmmuv">
            Resend OTP
          </button>}
      </div>
    </motion.div>;
}
// ─────────────────────────────────────────────────────────────────────────────
// DEVICE LINKING MODAL — same as in BOPSignupFlow
// ─────────────────────────────────────────────────────────────────────────────
function DeviceLinkingModal({
  onProceed
}: {
  onProceed: () => void;
}) {
  return <AnimatePresence data-id="element-1337" data-mp="1uvwkcssj3obo">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100]" style={{
      background: 'rgba(0,0,0,0.5)'
    }} data-id="element-1338" data-mp="1uvei9fj6qfvu" />

      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="fixed bottom-0 left-0 right-0 z-[101] max-w-[430px] mx-auto" style={{
      background: '#FFFFFF',
      borderTopLeftRadius: 28,
      borderTopRightRadius: 28,
      boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
      padding: '40px 28px 32px'
    }} data-id="element-1339" data-mp="1qw5jtxd79tty">
        {/* Chain icon */}
        <div className="flex justify-center mb-6" data-id="element-1340" data-mp="128wml2un0ua3">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1341" data-mp="1g9qtfh21en6n">
            <path d="M9.5 14.5L14.5 9.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" data-id="element-1342" data-mp="1q02a846tkquy" />

            <path d="M11 6.5L13 4.5C14.6569 2.84315 17.3431 2.84315 19 4.5C20.6569 6.15685 20.6569 8.84315 19 10.5L17 12.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-1343" data-mp="1l1r0dtgiygcq" />

            <path d="M13 17.5L11 19.5C9.34315 21.1569 6.65685 21.1569 5 19.5C3.34315 17.8431 3.34315 15.1569 5 13.5L7 11.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-1344" data-mp="2wdbnif0yvxbk" />
          </svg>
        </div>
        <h3 style={{
        color: '#1A1A1A',
        textAlign: 'center',
        marginBottom: 14
      }} className="text-[16px] font-[500]" data-id="element-1345" data-mp="2idspif1ve2cz">
          Mobile Device Linking
        </h3>
        <p style={{
        fontSize: 14,
        color: '#5A5A5A',
        textAlign: 'center',
        lineHeight: 1.6,
        marginBottom: 32,
        padding: '0 6px'
      }} data-id="element-1346" data-mp="23owkyrz0v7h4">
          Your device is being linked to the digiBOP Mobile Banking App. Please
          set up biometric for seamless access in the future.
        </p>
        <button onClick={onProceed} className="w-full active:scale-[0.98] transition-transform" style={{
        height: 56,
        borderRadius: 8,
        background: '#F87A27',
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer'
      }} data-id="element-1347" data-mp="57cpiasfz7gq">
          Proceed
        </button>
      </motion.div>
    </AnimatePresence>;
}
// ─────────────────────────────────────────────────────────────────────────────
// BVS SCREEN — same design as BOPSignupFlow BVS step
// ─────────────────────────────────────────────────────────────────────────────
function BVSScreen({
  isDark,
  isPlatinum,
  bvsPhase,
  setBvsPhase,
  bvsCameraTimer,
  setBvsCameraTimer,
  onComplete
}: {
  isDark: boolean;
  isPlatinum: boolean;
  bvsPhase: BvsPhase;
  setBvsPhase: (p: BvsPhase) => void;
  bvsCameraTimer: number;
  setBvsCameraTimer: (n: number) => void;
  onComplete: () => void;
}) {
  const textStyle = isPlatinum || isDark ? 'text-white' : 'text-[#1A1A1A]';
  const subtextStyle = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]';
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full flex-1 flex flex-col" style={{
    minHeight: '100vh'
  }} data-id="element-1348" data-mp="ksfmrp279eqn">
      <AnimatePresence mode="wait" data-id="element-1349" data-mp="3jauqce9tgjk5">
        {(bvsPhase === 'right_guidance' || bvsPhase === 'left_guidance') && <motion.div key={`guidance-${bvsPhase}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="flex-1 flex flex-col px-6" style={{
        minHeight: '100vh',
        background: isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF5F0'
      }} data-id="element-1350" data-mp="14yg1x0jhq3p">
            {/* Header */}
            <div className="relative flex items-center justify-between pt-6 pb-4" data-id="element-1351" data-mp="163mqgmzrd4rt">
              <button onClick={() => setBvsPhase(bvsPhase === 'right_guidance' ? 'right_guidance' : 'right_success')} className="p-2 -ml-2" data-id="element-1352" data-mp="165pmbnnlds4g">
                <ArrowLeftIcon className={`w-6 h-6 ${textStyle}`} strokeWidth={2} data-id="element-1353" data-mp="23t7zrbs77ies" />
              </button>

              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" className="h-12 w-auto pointer-events-none" data-id="element-1354" data-mp="1u094xc63yvmq" />

              <div className="w-10" data-id="element-1355" data-mp="ytjq5hlv5t87" />
            </div>

            <div className="flex items-center justify-center relative mb-8" data-id="element-1356" data-mp="yv4kp786hc62">
              <h2 className={`text-lg font-bold ${textStyle}`} data-id="element-1357" data-mp="ttf65hlqygd8">Guidance</h2>
            </div>
            <div className="text-center mb-8" data-id="element-1358" data-mp="oukgbvpd3rjm">
              <p className={`text-base leading-relaxed ${textStyle}`} data-id="element-1359" data-mp="oxgdhhtdyc70">
                Line up your hand with the guide
                <br data-id="element-1360" data-mp="1hk1cn4g3rsl4" />
                Keep your fingers together
                <br data-id="element-1361" data-mp="1hj9i7wy8s0wf" />
                Then stay still.
              </p>
            </div>

            <div className="flex-1 relative flex items-center justify-center" data-id="element-1362" data-mp="1clgcf50up8wo">
              <motion.div animate={{
            y: [-5, 5, -5]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative w-full max-w-[280px] aspect-[3/4] border-4 border-white/20 rounded-[40px] bg-black/10 overflow-hidden flex items-center justify-center" data-id="element-1363" data-mp="17t6h19gyvgn0">
                <div className={`w-3/4 h-1/2 opacity-50 ${bvsPhase === 'left_guidance' ? '-scale-x-100' : ''}`} data-id="element-1364" data-mp="23ut2sxpalpul">
                  <svg viewBox="0 0 100 100" className="w-full h-full fill-current text-[#F37024]" data-id="element-1365" data-mp="2cs3aya6tcyva">
                    <path d="M20,80 C20,50 40,30 40,30 L45,10 C45,5 55,5 55,10 L50,30 L60,15 C60,10 70,10 70,15 L60,35 L75,20 C75,15 85,15 85,20 L70,40 L85,30 C85,25 95,25 95,30 L75,60 C70,80 50,90 50,90 L20,80 Z" data-id="element-1366" data-mp="2mranww9myrhv" />
                  </svg>
                </div>
              </motion.div>
            </div>

            <div className="mt-8 pb-8" data-id="element-1367" data-mp="30ifteezoqe04">
              <h3 className={`text-center font-bold text-xl mb-6 uppercase tracking-widest ${textStyle}`} data-id="element-1368" data-mp="30he5vb1hfz1i">
                Scan your {bvsPhase === 'right_guidance' ? 'RIGHT' : 'LEFT'}{' '}
                hand
              </h3>
              <div className="flex justify-center" data-id="element-1369" data-mp="2kshs3skgb1kn">
                <button onClick={() => {
              setBvsCameraTimer(3);
              setBvsPhase(bvsPhase === 'right_guidance' ? 'right_camera' : 'left_camera');
            }} className="active:scale-[0.98] transition-transform bg-[#F37024]" style={{
              width: 278,
              height: 56,
              borderRadius: 8,
              background: '#F87A27',
              color: '#FFFFFF',
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 16,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 6px 16px rgba(102,24,6,0.25)'
            }} data-id="element-1370" data-mp="2kt9a20l4l0sg">
                  Continue
                </button>
              </div>
            </div>
          </motion.div>}

        {(bvsPhase === 'right_camera' || bvsPhase === 'left_camera') && <motion.div key={`camera-${bvsPhase}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="flex-1 flex flex-col bg-black relative" style={{
        minHeight: '100vh'
      }} data-id="element-1371" data-mp="3ustxbtmobby4">
            <div className="absolute inset-0 overflow-hidden" data-id="element-1372" data-mp="3b0nkuumtg8d5">
              <img src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=800&q=80" alt="camera feed" className="w-full h-full object-cover opacity-60" data-id="element-1373" data-mp="2x40mqoj4nc5n" />

              <div className="absolute inset-8 border-4 border-white rounded-[60px] opacity-80" data-id="element-1374" data-mp="v9t1ok56i5lz" />
              <motion.div animate={{
            top: ['10%', '90%', '10%']
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute left-8 right-8 h-1 bg-green-500 shadow-[0_0_15px_rgba(34,197,94,1)]" data-id="element-1375" data-mp="108cosnvidnfx" />
            </div>
            <div className="relative z-10 p-6 bg-gradient-to-b from-black/80 to-transparent" data-id="element-1376" data-mp="30iqtxrk5m75d">
              <div className="flex items-center justify-between mb-4" data-id="element-1377" data-mp="30ge4ktc2guia">
                <button onClick={() => setBvsPhase(bvsPhase === 'right_camera' ? 'right_guidance' : 'left_guidance')} className="text-white font-bold text-sm" data-id="element-1378" data-mp="2vi49sju97vx7">
                  Cancel
                </button>
                <span className="text-white font-bold text-sm" data-id="element-1379" data-mp="ks5n41un2d1f">
                  4 Fingers TouchlessID
                </span>
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" data-id="element-1380" data-mp="s9avbkwmrqtw" />
              </div>
              <div className="text-center text-white" data-id="element-1381" data-mp="x7vmyvxth26q">
                <p className="font-bold mb-1" data-id="element-1382" data-mp="1229kp4zyblek">
                  Place your {bvsPhase === 'right_camera' ? 'right' : 'left'}{' '}
                  hand in the box and stay still
                </p>
                <p className="text-sm opacity-80" data-id="element-1383" data-mp="9dksjvgviqey">Move hand further away</p>
              </div>
            </div>
          </motion.div>}

        {(bvsPhase === 'right_success' || bvsPhase === 'left_success') && <motion.div key={`success-${bvsPhase}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" style={{
        minHeight: '100vh'
      }} data-id="element-1384" data-mp="j95pvgazz2id">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} className={`w-full max-w-sm rounded-[32px] p-8 flex flex-col items-center text-center shadow-2xl ${isPlatinum ? 'bg-[#2A2A2C]' : isDark ? 'bg-[#2D1810]' : 'bg-white'}`} data-id="element-1385" data-mp="2wk3vu1exr9pa">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200
          }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 border-4 border-[#22C55E]/30" data-id="element-1386" data-mp="3faljhrkwvf3s">
                <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={2} data-id="element-1387" data-mp="1me989bsvm0nq" />
              </motion.div>
              <h2 className={`text-2xl font-bold mb-3 ${textStyle}`} data-id="element-1388" data-mp="1hlzka1bbnao3">
                {bvsPhase === 'right_success' ? 'Right Hand Scanned' : 'Left Hand Scanned'}
              </h2>
              <p className={`text-sm leading-relaxed mb-8 ${subtextStyle}`} data-id="element-1389" data-mp="18krrtzvhl41x">
                {bvsPhase === 'right_success' ? 'Great job! Your right hand was scanned successfully.' : 'All biometrics captured successfully.'}
              </p>
              <button onClick={() => {
            if (bvsPhase === 'right_success') setBvsPhase('left_guidance');
            if (bvsPhase === 'left_success') onComplete();
          }} className="active:scale-[0.98] transition-transform" style={{
            width: 278,
            height: 56,
            borderRadius: 8,
            background: '#F87A27',
            color: '#FFFFFF',
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 16,
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(102,24,6,0.25)'
          }} data-id="element-1390" data-mp="1wavti1p46tqa">
                Continue
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
// ─── LOGIN COOLING-OFF ─────────────────────────────────────────────────────
// Thin adapter over the shared CoolingOffScreen. The login flow supplies a
// `coolingOffUntil` timestamp and an `onOK` callback; CoolingOffScreen expects
// `coolingOffEndTime` and `onContinue`, so we map them here.
function LoginCoolingOffScreen({
  coolingOffUntil,
  onOK
}: {
  isDark: boolean;
  coolingOffUntil: number | null;
  onOK: () => void;
}) {
  return <CoolingOffScreen coolingOffEndTime={coolingOffUntil} onContinue={onOK} onBack={onOK} data-id="element-1391" data-mp="2x9qx354jzb06" />;
}